import { useCallback, useEffect, useMemo, useState } from 'react';
import { ITrainersData, ITrainersTool } from '@/types/trainers';
import {
  getNormalizedDetections,
  getNormalizedImages,
  getNormalizedPoints,
} from '@/utils/trainers';
import { DisplayOnMap } from '@/components/ui/trainers/shared/DisplayOnMap';
import ImageCarousel from '@/components/ui/trainers/ImageCarousel';
import { ApiErrorEnum } from '@/types/api';
import { ResponseType } from '@/components/admin/trainers/viewers/AdminWrapperObjectViewer';
import AzimuthSlider from '../shared/AzimuthSlider';

interface Props {
  data?: ITrainersData;
  tool?: ITrainersTool;
  actualResponse?: ResponseType;
  expectedResponse?: ResponseType;
  contentHeight?: number;
  modifyPosition: boolean;
  modifyAzimuth: boolean;
  showAzimuth: boolean;
  setData: (data: any) => void;
  lat: number;
  setLat: (lat: number) => void;
  lon: number;
  setLon: (lon: number) => void;
  azimuth: number;
  setAzimuth: (azimuth: number) => void;
}

const PositionAzimuth: React.FC<Props> = ({
  data,
  tool,
  actualResponse,
  expectedResponse,
  contentHeight,
  modifyPosition,
  modifyAzimuth,
  showAzimuth,
  setData,
  lat,
  setLat,
  lon,
  setLon,
  azimuth,
  setAzimuth,
}) => {
  const {
    leaseId = 'noleaseid',
    lat: initialLat,
    lon: initialLon,
    azimuth: initialAzimuth,
    frames,
    closeFrames,
    images: contentImages = [],
    detections,
    detectionsByFrame,
    imagesCoords,
  } = data?.content || {};
  const hasAppleMaps = !!tool?.args?.hasAppleMaps;
  const SVGOverlay = tool?.args?.svgOverlay;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageHeight, setImageHeight] = useState(300);

  // Images are either provided as a list of URLs (from content.images)
  // or as a list of frames (from content.frames) as IFrame[] or string[]
  const normalizedImages = useMemo(
    () => getNormalizedImages(frames, closeFrames, contentImages),
    [frames, contentImages, closeFrames],
  );

  // Points can be provided from content.imagesCoords or content.frames as well
  const normalizedPoints = useMemo(
    () => getNormalizedPoints(frames, closeFrames, imagesCoords),
    [imagesCoords, frames, closeFrames],
  );

  // Normalizing Detections
  const normalizedDetections = useMemo(
    () =>
      getNormalizedDetections(
        frames,
        closeFrames,
        detectionsByFrame,
        detections,
      ),
    [detections, frames, detectionsByFrame, closeFrames],
  );

  useEffect(() => {
    if (data && !data.error && !normalizedImages.length) {
      const error = ApiErrorEnum.NoContent;
      setData({ error });
    }
  }, [data, normalizedImages, setData]);

  useEffect(() => {
    setAzimuth(initialAzimuth);
    setLat(initialLat);
    setLon(initialLon);
  }, [initialAzimuth, initialLat, initialLon, setAzimuth, setLat, setLon]);

  const onAzimuthChange = (newAzimuth: number) => {
    setAzimuth(newAzimuth);
  };

  const onTargetDragEnd = useCallback(
    (lat: number, lng: number) => {
      if (lat && lng) {
        setLat(lat);
        setLon(lng);
      }
    },
    [setLat, setLon],
  );

  function getAzimuthFromResponse(response: ResponseType): number | undefined {
    if (!response) {
      return undefined;
    }
    if (typeof response === 'string') {
      return parseFloat(response);
    }
    if (response.azi) {
      return response.azi;
    }
    if (response.value) {
      return parseFloat(response.value);
    }
    if (response.annotations && response.annotations.length > 0) {
      if (typeof response.annotations[0] === 'string') {
        return parseFloat(response.annotations[0]);
      }
      return parseFloat(response.annotations[0].text);
    }
    return undefined;
  }

  const actualResponseCoords =
    actualResponse &&
    actualResponse.pos &&
    actualResponse.pos.coordinates &&
    actualResponse.pos.coordinates.length >= 2
      ? actualResponse.pos.coordinates
      : undefined;
  return (
    <>
      <div
        className="relative flex grow items-center overflow-hidden"
        style={{ height: contentHeight }}>
        <div className="mt-2 flex w-full items-start">
          <div className="relative h-full w-1/2">
            <ImageCarousel
              key={leaseId}
              images={normalizedImages}
              SVGOverlay={SVGOverlay}
              detections={normalizedDetections}
              onImageLoaded={setImageHeight}
              onSelectedImageChange={setSelectedImageIndex}
              startWithIndex="largestDetection"
            />
          </div>
          {imageHeight && leaseId && lat && lon ? (
            <div
              className="relative h-full w-1/2"
              style={{ height: imageHeight }}>
              <DisplayOnMap
                target={{
                  lat,
                  lon,
                  ...((showAzimuth || modifyAzimuth) && { azimuth }),
                }}
                actualTarget={
                  actualResponse
                    ? {
                        lat: actualResponseCoords
                          ? (actualResponseCoords[1] as number)
                          : showAzimuth
                            ? lat
                            : 0,
                        lon: actualResponseCoords
                          ? (actualResponseCoords[0] as number)
                          : showAzimuth
                            ? lon
                            : 0,
                        ...((showAzimuth || modifyAzimuth) && {
                          azimuth: getAzimuthFromResponse(actualResponse),
                        }),
                      }
                    : undefined
                }
                expectedTarget={
                  expectedResponse
                    ? {
                        lat: expectedResponse.pos?.coordinates[1]
                          ? expectedResponse.pos?.coordinates[1]
                          : showAzimuth
                            ? lat
                            : 0,
                        lon: expectedResponse.pos?.coordinates[0]
                          ? expectedResponse.pos?.coordinates[0]
                          : showAzimuth
                            ? lon
                            : 0,
                        ...((showAzimuth || modifyAzimuth) && {
                          azimuth: getAzimuthFromResponse(expectedResponse),
                        }),
                      }
                    : undefined
                }
                points={normalizedPoints}
                onTargetDragEnd={modifyPosition ? onTargetDragEnd : undefined}
                options={{
                  hasAppleMaps,
                  selectedCameraIndex: selectedImageIndex,
                  alignViewBasedOnPoints: true,
                  hideTargetMarker: tool?.widget === 'picker',
                }}
                hypotheses={
                  tool?.widget === 'picker'
                    ? data?.content?.positionHypotheses ?? []
                    : []
                }
              />
              {modifyAzimuth ? (
                <div className="px-6 py-1">
                  <AzimuthSlider
                    azimuth={azimuth}
                    initialAzimuth={initialAzimuth}
                    updateAzimuth={onAzimuthChange}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default PositionAzimuth;
